import { Link } from "react-router-dom";
import { Calendar, Gauge, Fuel } from "lucide-react";

// Fuel → pill color mapping (green for electric/hybrid, dark gray for combustion)
const FUEL_COLORS = {
  Elettrico: "bg-[#5ab500] text-white",
  Ibrido: "bg-[#5ab500]/85 text-white",
  Benzina: "bg-[#1c1f1a] text-white",
  Diesel: "bg-[#1c1f1a] text-white",
  GPL: "bg-[#4a4f47] text-white",
  Metano: "bg-[#4a4f47] text-white",
};

export default function VehicleCard({ vehicle }) {
  const fallback =
    "https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&w=1200";
  const image = vehicle.images?.[0] || fallback;

  const fmtPrice = new Intl.NumberFormat("it-IT").format(vehicle.price);
  const fmtKm = new Intl.NumberFormat("it-IT").format(vehicle.km);
  const pillCls =
    FUEL_COLORS[vehicle.fuel] || "bg-[#1c1f1a] text-white";

  return (
    <Link
      to={`/vehicle/${vehicle.id}`}
      data-testid={`vehicle-card-${vehicle.id}`}
      className="group card-soft block rounded-2xl overflow-hidden border border-black/[0.05]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#f5f6f4]">
        <img
          src={image}
          alt={`${vehicle.brand} ${vehicle.model}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {/* Fuel pill (top-left) */}
        <div className="absolute top-3 left-3">
          <span
            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md ${pillCls}`}
          >
            {vehicle.fuel}
          </span>
        </div>
        {/* Type pill (top-right) */}
        <div className="absolute top-3 right-3">
          <span
            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md backdrop-blur-md ${
              vehicle.type === "scooter_cabinato"
                ? "bg-white/95 text-[#5ab500] border border-[#5ab500]/20"
                : "bg-white/95 text-[#1c1f1a] border border-black/10"
            }`}
          >
            {vehicle.type === "scooter_cabinato" ? "Scooter" : "Auto"}
          </span>
        </div>
        {/* "NUOVO" badge — appears only when condition === "Nuovo" */}
        {vehicle.condition === "Nuovo" && (
          <div className="absolute bottom-3 left-3">
            <span
              data-testid="badge-nuovo"
              className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-md bg-[#5ab500] text-white"
            >
              Nuovo
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#7a7f77] font-bold mb-1">
          {vehicle.brand}
        </div>
        <h3 className="font-heading text-lg md:text-xl font-semibold text-[#1c1f1a] truncate group-hover:text-[#5ab500] transition-colors">
          {vehicle.model}
        </h3>

        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-2xl md:text-3xl font-heading font-bold text-[#5ab500]">
            € {fmtPrice}
          </span>
        </div>

        <div className="mt-4 pt-4 border-t border-black/[0.06] flex items-center gap-3 text-xs text-[#4a4f47]">
          <span className="flex items-center gap-1.5">
            <Calendar size={12} className="text-[#7a7f77]" />
            {vehicle.year}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#b0b3ad]" />
          <span className="flex items-center gap-1.5">
            <Gauge size={12} className="text-[#7a7f77]" />
            {fmtKm} km
          </span>
          <span className="w-1 h-1 rounded-full bg-[#b0b3ad]" />
          <span className="flex items-center gap-1.5">
            <Fuel size={12} className="text-[#7a7f77]" />
            {vehicle.fuel}
          </span>
        </div>
      </div>
    </Link>
  );
}
